/**
 * PlayerService — camada de "rede" do POC.
 *
 * Orquestra a dinâmica do jogo: monta o grafo de ruas, os avatares
 * (CityAgents, com a máquina de estados) e o backend SIMULADO que os dirige.
 * Emite snapshots de coordenadas em um tick fixo — o MESMO formato que o
 * backend real entregará via websocket:
 *
 *   snapshot = { t, players: [{ id, name, type, color, x, z, heading, speed, state }] }
 *
 * Para plugar o backend real, troque o SimulatedBackend por um adaptador de
 * websocket que chame os mesmos métodos do CityAgents
 * (spawn/goto/actions/complete/leave). EntityManager e renderer não mudam.
 */
import { RoadGraph } from '../world/RoadGraph.js';
import { SidewalkGraph } from '../world/SidewalkGraph.js';
import { CityAgents } from './CityAgents.js';
import { SimulatedBackend } from './SimulatedBackend.js';
import { RealBackend } from './RealBackend.js';
import { CITY_ID } from '../world/cityLayout.js';

// Teto de sub-passos por tick (ver _tick). 8 × 120 ms = ~1 s de recuperação por chamada:
// segura um engasgo de quadro sem transformar a volta de uma aba parada há horas num
// travamento — nesse caso o excedente é descartado de propósito.
const MAX_SUBPASSOS = 8;
// Quanto tempo escondida justifica pedir o estado ao servidor na volta (ver
// _observarVisibilidade). Abaixo disso os sub-passos do _tick já reassentam sozinhos.
const OCULTA_PARA_RESYNC_MS = 30000;

export class PlayerService {
  constructor() {
    this._listeners = new Set();
    this._tickMs = 120; // ritmo da simulação (interpolado no render); mais alto =
    // curvas mais suaves e reações mais ágeis (menos "travadas" no congestionamento)
    this._timer = null;
    this._lastTick = 0;
    this._population = 5;
  }

  /** Inscreve um callback para receber snapshots. Retorna unsubscribe. */
  onSnapshot(cb) {
    this._listeners.add(cb);
    return () => this._listeners.delete(cb);
  }

  emit(snapshot) {
    for (const cb of this._listeners) cb(snapshot);
  }

  /** Monta a dinâmica e inicia a simulação. (mantém a assinatura async) */
  /** `spectator`: a tela só ASSISTE (o /me do jogador) — recebe tudo, não reporta nada. */
  async connect({ spectator = false } = {}) {
    this.roadGraph = new RoadGraph();
    this.walkGraph = new SidewalkGraph();
    this.agents = new CityAgents(this.roadGraph, this.walkGraph);

    // PADRÃO: backend REAL — o jogo carrega VAZIO e espera os jogadores entrarem
    // pelo backend (ws://host:8787, ou ?backend=ws://… p/ outra URL). O modo
    // SIMULADO (jogadores fake p/ demo) só é ativado com ?sim na URL.
    // Os dois têm a mesma interface (setTarget/tick) — o resto do jogo não muda.
    const params = new URLSearchParams(location.search);
    const sim = params.has('sim');
    this.live = !sim;
    this.backend = sim
      ? new SimulatedBackend(this.agents)
      : new RealBackend(this.agents, { url: params.get('backend') || undefined, city: CITY_ID, spectator });

    // população só importa no modo simulado: ?players=N (atalho) ou padrão (5).
    // No modo ao vivo começa em 0 (ninguém até o backend mandar entrar).
    const fromUrl = Number(params.get('players'));
    this._population = sim ? (Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 5) : 0;
    this.backend.setTarget(this._population);

    this._startSimulation();
    return [];
  }

  disconnect() {
    if (this._timer) clearInterval(this._timer);
    this._timer = null;
    this.backend?.disconnect?.(); // RealBackend fecha o socket; Simulado não tem
  }

  /** Redimensiona a META de população (entram/saem para atingir o número). */
  setPopulation(n) {
    this._population = Math.max(1, Math.min(2000, Math.round(n)));
    this.backend?.setTarget(this._population);
    return this._population;
  }

  /** Liga os hooks de reação (emote/say) ao renderer. No-op no backend simulado. */
  setEffects(fx) {
    this.backend?.setEffects?.(fx);
  }

  /** "Visão do jogador": resolve a chave opaca (?follow=<key>) para o id interno do
   *  avatar vivo, ou null. No backend simulado não há followKey → sempre null. */
  resolveFollow(key) {
    return this.backend?.resolveFollow?.(key) ?? null;
  }

  get population() {
    return this._population;
  }

  get tickMs() {
    return this._tickMs;
  }

  /* ------------------ simulação interna ------------------ */

  _startSimulation() {
    this._lastTick = performance.now();
    this._acumulado = 0;
    // FALLBACK LENTO, não o motor: o trabalho pesado do tick (CityAgents.update =
    // 2,3 ms com 500 agentes + emissão do snapshot) rodava aqui a cada 120 ms —
    // FORA do quadro de render. Essa tarefa entre dois quadros de ~12 ms atrasava
    // o seguinte e estourava o slot do vsync ~8×/s: os avatares andavam "com
    // travadinhas" mesmo a 82 fps, e o medidor não acusava nada porque só mede o
    // que roda DENTRO do rAF. O motor agora é pump(), chamado pelo loop de render
    // (main.js/me.js) — a simulação divide o orçamento do quadro em vez de brigar
    // com ele. O intervalo fica só para a ABA OCULTA (rAF parado): mantém o
    // backend drenando e a cidade andando devagar (o browser já limita intervalos
    // de fundo a ~1 Hz; o acumulador com MAX_SUBPASSOS absorve a volta).
    this._timer = setInterval(() => this._tick(), 1000);
    this._observarVisibilidade();
    this._tick();
  }

  /**
   * Motor da simulação, chamado UMA vez por quadro de render (stage.addUpdater).
   * O acumulador interno já emite apenas quando fecha um passo de tickMs — chamar
   * em qualquer frequência (82 fps, 30 fps com ?fps, ou o fallback de 1 Hz da aba
   * oculta) produz a MESMA cadência de snapshots.
   */
  pump() {
    // sem simulação iniciada (connect falhou/ainda não rodou) não há o que bombear —
    // o mesmo contrato do setInterval antigo, que só nascia no _startSimulation
    if (!this._timer) return;
    this._tick();
  }

  /**
   * VOLTA DE UMA ABA QUE FICOU ESCONDIDA: reassenta a cidade pelo SERVIDOR.
   *
   * Os sub-passos do `_tick` recuperam o tempo perdido enquanto o navegador ainda dá
   * ticks (aba oculta a 1 Hz: entrega 96% do relógio, medido). Mas sob o throttling
   * INTENSIVO do Chrome — aba escondida há mais de 5 min, timer uma vez por minuto — não
   * há passo lógico que dê conta: entregar 60 s de simulação numa chamada travaria a aba,
   * e o teto de sub-passos deixa a entrega em ~1,6%. É o celular no bolso durante o
   * evento, ou seja, a maioria das ~200 telas /me de cada cidade.
   *
   * Como ENTRAR é comando do servidor e SAIR é navegação local, a divergência é sempre no
   * mesmo sentido: o jogador desbloqueia o telefone e vê a cidade entupida de gente que
   * já deveria ter ido embora. O servidor tem a população correta, e o snapshot dele já
   * sabe expurgar quem não existe mais — então a saída certa é pedir o estado de novo, e
   * não tentar simular horas em um quadro.
   *
   * Só depois de um sumiço LONGO: alternar de aba por alguns segundos não justifica
   * rebaixar o estado local nem custar um snapshot ao gateway.
   */
  _observarVisibilidade() {
    if (typeof document === 'undefined' || !document.addEventListener) return;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { this._escondidaEm = performance.now(); return; }
      const fora = this._escondidaEm ? performance.now() - this._escondidaEm : 0;
      this._escondidaEm = 0;
      // o acumulado do sumiço não deve virar uma rajada de sub-passos na volta
      this._acumulado = 0;
      this._lastTick = performance.now();
      if (fora > OCULTA_PARA_RESYNC_MS) this.backend.resync?.('voltou-de-aba-oculta');
    });
  }

  /**
   * PASSO LÓGICO FIXO, em vez de um dt que segue o relógio da aba.
   *
   * `setInterval` de 120 ms vira 1 s numa aba oculta — e 1 min sob o throttling intensivo
   * do Chrome, depois de 5 min escondida. Com o dt cortado em 0,25 s a simulação
   * simplesmente PERDIA esse tempo. E o desequilíbrio é assimétrico: ENTRAR é comando do
   * servidor (chega em tempo real), SAIR é navegação local (anda na velocidade da
   * simulação). MEDIDO em 30 min a 20 entradas/min: aba visível 570 saídas; aba oculta a
   * 1 Hz, 559; sob throttling intensivo, 82 de 600. É o caso das ~200 telas /me por
   * cidade (celular no bolso) e de um telão que seja ocluído por protetor de tela — que é
   * justamente quem reporta os `left`.
   *
   * Agora o tempo decorrido é acumulado e consumido em sub-passos do tamanho do tick.
   * Nada de dt gigante (a física continua estável) e nada de tempo perdido, com um teto
   * de sub-passos por chamada para a volta de uma aba parada há horas não travar o
   * quadro — o excedente é descartado de propósito.
   */
  _tick() {
    const now = performance.now();
    const passo = this._tickMs / 1000;
    this._acumulado = (this._acumulado || 0) + (now - this._lastTick) / 1000;
    this._lastTick = now;

    let n = Math.floor(this._acumulado / passo);
    if (n > MAX_SUBPASSOS) { n = MAX_SUBPASSOS; this._acumulado = 0; } // volta de aba parada: não recupera horas
    else this._acumulado -= n * passo;
    if (n < 1) return;               // ainda não fechou um passo: não emite quadro repetido

    // O backend decide entradas/saídas/comandos SÓ quando um passo fecha — a mesma
    // cadência (~tickMs) de quando o motor era o setInterval. Com o pump por quadro,
    // rodá-lo ANTES do corte o levava a 60–82 Hz e mudava taxas por-chamada: as
    // SAÍDAS acima da meta viravam êxodo (82/s em vez de 8,3/s ao baixar o slider)
    // e o retry de entradas pendentes do RealBackend martelava o spawn 10× mais.
    this.backend.tick(now);

    let players;
    for (let i = 0; i < n; i++) players = this.agents.update(passo); // avatares se movem e estacionam
    // JANELA DE INTERPOLAÇÃO REAL. O EntityManager interpola em `snapDuration` e satura
    // em 1, então uma janela CONSTANTE contra um produtor que só pode ATRASAR congelava
    // todos os avatares no alvo durante o atraso — medido, 3,7% de cada tick, sem
    // compensação possível e piorando conforme a cidade enche e o quadro fica mais pesado.
    //
    // A janela é o intervalo REAL entre quadros (medido de emissão a emissão), não
    // `n × tickMs`: o nominal é sempre múltiplo de 120 ms e não enxerga justamente o
    // atraso que causa o congelamento. Média móvel curta para o valor não pular com um
    // engasgo isolado, e limites para o caso degenerado da aba que ficou minutos parada —
    // ali a janela viraria minutos e travaria a cidade inteira.
    const real = this._ultimoEmit ? now - this._ultimoEmit : n * this._tickMs;
    this._ultimoEmit = now;
    const janela = Math.max(this._tickMs * 0.5, Math.min(real, this._tickMs * 4));
    this._janelaMs = this._janelaMs ? this._janelaMs * 0.8 + janela * 0.2 : janela;
    this.emit({ t: Date.now(), players, janelaMs: this._janelaMs });
  }
}
